import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import managementService from '../services/managementService';
import {
  Users, Shield, Warehouse, Building2, Package, Settings,
  Plus, Edit, Trash2, Search, Filter, CheckCircle, XCircle,
  Mail, Phone, MapPin, Star, TrendingUp, AlertCircle, X, Loader
} from 'lucide-react';
import { useToast, SmartDropdown } from '../components/ui';

const Management = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const queryClient = useQueryClient();

  // Queries
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => managementService.getUsers()
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => managementService.getRoles()
  });

  const { data: docks = [], isLoading: docksLoading } = useQuery({
    queryKey: ['docks'],
    queryFn: () => managementService.getDocks()
  });

  const { data: suppliers = [], isLoading: suppliersLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => managementService.getSuppliers()
  });

  const { data: materialTypes = [], isLoading: materialsLoading } = useQuery({
    queryKey: ['materialTypes'],
    queryFn: () => managementService.getMaterialTypes()
  });

  const { data: settings = {}, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => managementService.getSettings()
  });

  // User Mutations
  const createUserMutation = useMutation({
    mutationFn: (userData) => managementService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSuccess('User created successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create user');
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => managementService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSuccess('User updated successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to update user');
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id) => managementService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSuccess('User deleted successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to delete user');
    }
  });

  // Role Mutations
  const createRoleMutation = useMutation({
    mutationFn: (roleData) => managementService.createRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setSuccess('Role created successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create role');
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }) => managementService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setSuccess('Role updated successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to update role');
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id) => managementService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setSuccess('Role deleted successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to delete role');
    }
  });

  // Dock Mutations
  const createDockMutation = useMutation({
    mutationFn: (dockData) => managementService.createDock(dockData),
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSuccess('Dock created successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create dock');
    }
  });

  const updateDockMutation = useMutation({
    mutationFn: ({ id, data }) => managementService.updateDock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSuccess('Dock updated successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to update dock');
    }
  });

  const deleteDockMutation = useMutation({
    mutationFn: (id) => managementService.deleteDock(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSuccess('Dock deleted successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to delete dock');
    }
  });

  // Supplier Mutations
  const createSupplierMutation = useMutation({
    mutationFn: (supplierData) => managementService.createSupplier(supplierData),
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliers']);
      setSuccess('Supplier created successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create supplier');
    }
  });

  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, data }) => managementService.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliers']);
      setSuccess('Supplier updated successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to update supplier');
    }
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: (id) => managementService.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliers']);
      setSuccess('Supplier deleted successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to delete supplier');
    }
  });

  // Material Type Mutations
  const createMaterialMutation = useMutation({
    mutationFn: (materialData) => managementService.createMaterialType(materialData),
    onSuccess: () => {
      queryClient.invalidateQueries(['materialTypes']);
      setSuccess('Material type created successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create material type');
    }
  });

  const updateMaterialMutation = useMutation({
    mutationFn: ({ id, data }) => managementService.updateMaterialType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['materialTypes']);
      setSuccess('Material type updated successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to update material type');
    }
  });

  const deleteMaterialMutation = useMutation({
    mutationFn: (id) => managementService.deleteMaterialType(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['materialTypes']);
      setSuccess('Material type deleted successfully!');
      closeModal();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to delete material type');
    }
  });

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
    { id: 'docks', label: 'Dock Configuration', icon: Warehouse },
    { id: 'suppliers', label: 'Suppliers & Vendors', icon: Building2 },
    { id: 'materials', label: 'Material Types', icon: Package },
    { id: 'settings', label: 'System Settings', icon: Settings }
  ];

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    setError('');
    setSuccess('');

    // Pre-populate form data if editing
    if (item) {
      if (type === 'editUser') {
        setFormData({
          first_name: item.name?.split(' ')[0] || '',
          last_name: item.name?.split(' ').slice(1).join(' ') || '',
          email: item.email || '',
          username: item.username || '',
          phone: item.phone || '',
          role: item.role || '',
          status: item.status || 'ACTIVE'
        });
      } else if (type === 'editRole') {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          permissions: item.permissions || []
        });
      } else if (type === 'editDock') {
        setFormData({
          number: item.number || '',
          capacity: item.capacity || '',
          hasForklift: item.hasForklift || false,
          hasWeighbridge: item.hasWeighbridge || false,
          status: item.status || 'ACTIVE',
          dock_type: item.type || 'LOADING'
        });
      } else if (type === 'editSupplier') {
        setFormData({
          name: item.name || '',
          code: item.code || '',
          contact_person: item.contact || '',
          email: item.email || '',
          phone: item.phone || '',
          rating: item.rating || 0,
          status: item.status || 'ACTIVE'
        });
      } else if (type === 'editMaterial') {
        setFormData({
          name: item.name || '',
          code: item.code || '',
          sla_hours: item.slaHours || '',
          qc_required: item.qcRequired || false,
          priority: item.priority || 'NORMAL',
          color: item.color || 'blue'
        });
      }
    } else {
      // Reset form for add operations
      setFormData({});
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setFormData({});
    setError('');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // User operations
    if (modalType === 'addUser') {
      createUserMutation.mutate(formData);
    } else if (modalType === 'editUser') {
      updateUserMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'deleteUser') {
      deleteUserMutation.mutate(selectedItem.id);
    }
    // Role operations
    else if (modalType === 'addRole') {
      createRoleMutation.mutate(formData);
    } else if (modalType === 'editRole') {
      updateRoleMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'deleteRole') {
      deleteRoleMutation.mutate(selectedItem.id);
    }
    // Dock operations
    else if (modalType === 'addDock') {
      createDockMutation.mutate(formData);
    } else if (modalType === 'editDock') {
      updateDockMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'deleteDock') {
      deleteDockMutation.mutate(selectedItem.id);
    }
    // Supplier operations
    else if (modalType === 'addSupplier') {
      createSupplierMutation.mutate(formData);
    } else if (modalType === 'editSupplier') {
      updateSupplierMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'deleteSupplier') {
      deleteSupplierMutation.mutate(selectedItem.id);
    }
    // Material Type operations
    else if (modalType === 'addMaterial') {
      createMaterialMutation.mutate(formData);
    } else if (modalType === 'editMaterial') {
      updateMaterialMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'deleteMaterial') {
      deleteMaterialMutation.mutate(selectedItem.id);
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const isLoading = () => {
    return createUserMutation.isLoading || updateUserMutation.isLoading || deleteUserMutation.isLoading ||
           createRoleMutation.isLoading || updateRoleMutation.isLoading || deleteRoleMutation.isLoading ||
           createDockMutation.isLoading || updateDockMutation.isLoading || deleteDockMutation.isLoading ||
           createSupplierMutation.isLoading || updateSupplierMutation.isLoading || deleteSupplierMutation.isLoading ||
           createMaterialMutation.isLoading || updateMaterialMutation.isLoading || deleteMaterialMutation.isLoading;
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Management</h1>
        <p className="text-gray-600 mt-2">Manage users, roles, configurations, and system settings</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => openModal('addUser')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add User</span>
                </button>
              </div>

              {usersLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{user.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="w-4 h-4 mr-2" />
                              {user.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="w-4 h-4 mr-2" />
                              {user.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {user.role_name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openModal('editUser', user)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openModal('deleteUser', user)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Roles & Permissions</h3>
                <button
                  onClick={() => openModal('addRole')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Role</span>
                </button>
              </div>

              {rolesLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {roles.map((role) => (
                    <div key={role.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{role.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openModal('editRole', role)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('deleteRole', role)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="w-4 h-4 mr-2" />
                          <span>{role.users} user(s) assigned</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {role.permissions.map((perm, idx) => (
                            <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Docks Tab */}
          {activeTab === 'docks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Dock Configuration</h3>
                <button
                  onClick={() => openModal('addDock')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Dock</span>
                </button>
              </div>

              {docksLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {docks.map((dock) => (
                    <div key={dock.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">{dock.number}</h4>
                          <p className="text-sm text-gray-600">Capacity: {dock.capacity}T</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(dock.status)}`}>
                          {dock.status}
                        </span>
                      </div>
                      <div className="space-y-2 mb-3">
                        <div className="text-sm text-gray-600">Type: {dock.type}</div>
                        <div className="flex items-center space-x-4 text-xs">
                          <span className={dock.hasForklift ? 'text-green-600' : 'text-gray-400'}>
                            {dock.hasForklift ? '✓' : '✗'} Forklift
                          </span>
                          <span className={dock.hasWeighbridge ? 'text-green-600' : 'text-gray-400'}>
                            {dock.hasWeighbridge ? '✓' : '✗'} Weighbridge
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openModal('editDock', dock)}
                          className="flex-1 text-blue-600 hover:text-blue-800 text-sm border border-blue-600 rounded px-3 py-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openModal('deleteDock', dock)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Suppliers Tab */}
          {activeTab === 'suppliers' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search suppliers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => openModal('addSupplier')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Supplier</span>
                </button>
              </div>

              {suppliersLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deliveries</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On-Time %</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {suppliers.map((supplier) => (
                        <tr key={supplier.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{supplier.name}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {supplier.code}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{supplier.contact}</div>
                            <div className="text-xs text-gray-500">{supplier.email}</div>
                            <div className="text-xs text-gray-500">{supplier.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`flex items-center font-semibold ${getRatingColor(supplier.rating)}`}>
                              <Star className="w-4 h-4 mr-1 fill-current" />
                              {supplier.rating}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {supplier.totalDeliveries}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <TrendingUp className={`w-4 h-4 mr-1 ${supplier.onTimeRate >= 90 ? 'text-green-600' : 'text-yellow-600'}`} />
                              <span className={supplier.onTimeRate >= 90 ? 'text-green-600' : 'text-yellow-600'}>
                                {supplier.onTimeRate}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(supplier.status)}`}>
                              {supplier.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openModal('editSupplier', supplier)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openModal('deleteSupplier', supplier)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Material Types Tab */}
          {activeTab === 'materials' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Material Types & Categories</h3>
                <button
                  onClick={() => openModal('addMaterial')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Material Type</span>
                </button>
              </div>

              {materialsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {materialTypes.map((material) => (
                    <div key={material.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="text-lg font-semibold text-gray-900">{material.name}</h4>
                            <span className={`px-2 py-1 bg-${material.color}-100 text-${material.color}-800 rounded text-xs font-medium`}>
                              {material.code}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">SLA: {material.slaHours}h</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openModal('editMaterial', material)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('deleteMaterial', material)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Priority:</span>
                          <span className="font-semibold">{material.priority}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">QC Required:</span>
                          {material.qcRequired ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* System Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">System Settings</h3>

              {settingsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Settings display */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-900">General Settings</h4>
                      {Object.keys(settings).length > 0 ? (
                        Object.entries(settings).map(([key, value]) => (
                          <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </label>
                            <input
                              type="text"
                              value={String(value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              readOnly
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No settings configured</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Settings are currently read-only</p>
                      <p className="text-xs text-yellow-700 mt-1">Contact administrator to modify system settings</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Delete Confirmation Modal */}
            {modalType.startsWith('delete') && (
              <>
                <h2 className="text-2xl font-bold mb-4 text-red-600">Confirm Delete</h2>
                <p className="mb-6 text-gray-700">
                  Are you sure you want to delete this {modalType.replace('delete', '').toLowerCase()}?
                  This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={isLoading()}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
                    disabled={isLoading()}
                  >
                    {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                    <span>{isLoading() ? 'Deleting...' : 'Delete'}</span>
                  </button>
                </div>
              </>
            )}

            {/* Add/Edit User Modal */}
            {(modalType === 'addUser' || modalType === 'editUser') && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'addUser' ? 'Add New User' : 'Edit User'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.first_name || ''}
                      onChange={(e) => handleFormChange('first_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.last_name || ''}
                      onChange={(e) => handleFormChange('last_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Username *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.username || ''}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      disabled={modalType === 'editUser'}
                    />
                  </div>
                  {modalType === 'addUser' && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Password *</label>
                      <input
                        type="password"
                        required
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.password || ''}
                        onChange={(e) => handleFormChange('password', e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.phone || ''}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Role *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.role || ''}
                      onChange={(e) => handleFormChange('role', e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.status || 'ACTIVE'}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={isLoading()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      disabled={isLoading()}
                    >
                      {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{isLoading() ? 'Saving...' : modalType === 'addUser' ? 'Create User' : 'Update User'}</span>
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Add/Edit Role Modal */}
            {(modalType === 'addRole' || modalType === 'editRole') && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'addRole' ? 'Add New Role' : 'Edit Role'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Role Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <textarea
                      required
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.description || ''}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permissions <span className="text-red-500">*</span>
                    </label>
                    <select
                      multiple
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={Array.isArray(formData.permissions) ? formData.permissions : []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleFormChange('permissions', selected);
                      }}
                      size="6"
                    >
                      <option value="dashboard">Dashboard - View system dashboard</option>
                      <option value="gate_entry">Gate Entry - Manage truck arrivals</option>
                      <option value="dock_management">Dock Management - Assign and manage docks</option>
                      <option value="unloading">Unloading - Monitor unloading operations</option>
                      <option value="qc_inspection">QC Inspection - Perform quality checks</option>
                      <option value="grn_creation">GRN Creation - Create goods receipt notes</option>
                      <option value="reports">Reports - View and generate reports</option>
                      <option value="user_management">User Management - Manage users and roles</option>
                      <option value="workflow_config">Workflow Configuration - Configure workflows</option>
                      <option value="access_control">Access Control - Manage permissions</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple permissions</p>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={isLoading()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      disabled={isLoading()}
                    >
                      {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{isLoading() ? 'Saving...' : modalType === 'addRole' ? 'Create Role' : 'Update Role'}</span>
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Add/Edit Dock Modal */}
            {(modalType === 'addDock' || modalType === 'editDock') && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'addDock' ? 'Add New Dock' : 'Edit Dock'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Dock Number *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.number || ''}
                      onChange={(e) => handleFormChange('number', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Capacity (tonnes) *</label>
                    <input
                      type="number"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.capacity || ''}
                      onChange={(e) => handleFormChange('capacity', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Dock Type *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.dock_type || 'LOADING'}
                      onChange={(e) => handleFormChange('dock_type', e.target.value)}
                    >
                      <option value="LOADING">Loading</option>
                      <option value="UNLOADING">Unloading</option>
                      <option value="BOTH">Both</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hasForklift"
                      checked={formData.hasForklift || false}
                      onChange={(e) => handleFormChange('hasForklift', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="hasForklift" className="text-sm font-medium">Has Forklift</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hasWeighbridge"
                      checked={formData.hasWeighbridge || false}
                      onChange={(e) => handleFormChange('hasWeighbridge', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="hasWeighbridge" className="text-sm font-medium">Has Weighbridge</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.status || 'ACTIVE'}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="MAINTENANCE">Maintenance</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={isLoading()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      disabled={isLoading()}
                    >
                      {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{isLoading() ? 'Saving...' : modalType === 'addDock' ? 'Create Dock' : 'Update Dock'}</span>
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Add/Edit Supplier Modal */}
            {(modalType === 'addSupplier' || modalType === 'editSupplier') && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'addSupplier' ? 'Add New Supplier' : 'Edit Supplier'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Supplier Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Supplier Code *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.code || ''}
                      onChange={(e) => handleFormChange('code', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Contact Person *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.contact_person || ''}
                      onChange={(e) => handleFormChange('contact_person', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <input
                      type="tel"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.phone || ''}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Rating (0-5)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.rating || 0}
                      onChange={(e) => handleFormChange('rating', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.status || 'ACTIVE'}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="WARNING">Warning</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={isLoading()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      disabled={isLoading()}
                    >
                      {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{isLoading() ? 'Saving...' : modalType === 'addSupplier' ? 'Create Supplier' : 'Update Supplier'}</span>
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Add/Edit Material Type Modal */}
            {(modalType === 'addMaterial' || modalType === 'editMaterial') && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'addMaterial' ? 'Add New Material Type' : 'Edit Material Type'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Material Name *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Material Code *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.code || ''}
                      onChange={(e) => handleFormChange('code', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">SLA Hours *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.sla_hours || ''}
                      onChange={(e) => handleFormChange('sla_hours', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Priority *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.priority || 'NORMAL'}
                      onChange={(e) => handleFormChange('priority', e.target.value)}
                    >
                      <option value="LOW">Low</option>
                      <option value="NORMAL">Normal</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Color</label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.color || 'blue'}
                      onChange={(e) => handleFormChange('color', e.target.value)}
                    >
                      <option value="red">Red</option>
                      <option value="orange">Orange</option>
                      <option value="yellow">Yellow</option>
                      <option value="green">Green</option>
                      <option value="blue">Blue</option>
                      <option value="purple">Purple</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="qc_required"
                      checked={formData.qc_required || false}
                      onChange={(e) => handleFormChange('qc_required', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="qc_required" className="text-sm font-medium">QC Required</label>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={isLoading()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                      disabled={isLoading()}
                    >
                      {isLoading() && <Loader className="w-4 h-4 animate-spin" />}
                      <span>{isLoading() ? 'Saving...' : modalType === 'addMaterial' ? 'Create Material Type' : 'Update Material Type'}</span>
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Management;
